import React, { useEffect, useState } from 'react';
import api from '../api';
import DrillDownModal from '../components/DrillDownModal';
import { CurrencyCircleDollar, FunnelSimple, CheckCircle, Clock, Warning, Leaf } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';

const TAX_COLORS = { 'Green': '#2D7D46', 'Amber': '#D9943B', 'Red': '#C6493A' };
const STATUS_COLORS = { 'Approved': '#2D7D46', 'Screening': '#D9943B', 'Pipeline': '#87A98E', 'Disbursed': '#0A4D3C' };
const COLORS = ['#0A4D3C', '#C2714F', '#D9943B', '#87A98E', '#0E6B53'];

export default function GreenFinance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillKey, setDrillKey] = useState(null);

  useEffect(() => {
    api.get('/api/dashboard/green-finance').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="animate-pulse text-content-secondary">Loading green finance data...</div>;
  if (!data) return <div className="text-status-alert">Failed to load</div>;

  const { projects, reference_metrics } = data;
  const totalValue = projects.reduce((a, p) => a + (p.amount_php_mn || 0), 0);
  const approvedValue = projects.filter(p => p.status === 'Approved').reduce((a, p) => a + (p.amount_php_mn || 0), 0);
  const adaptationValue = projects.filter(p => p.use_case === 'Adaptation').reduce((a, p) => a + (p.amount_php_mn || 0), 0);
  const greenCount = projects.filter(p => p.taxonomy_class === 'Green').length;

  // By taxonomy
  const taxData = {};
  projects.forEach(p => { taxData[p.taxonomy_class] = (taxData[p.taxonomy_class] || 0) + (p.amount_php_mn || 0); });
  const taxPie = Object.entries(taxData).map(([name, value]) => ({ name, value }));

  // By use case
  const useData = {};
  projects.forEach(p => { useData[p.use_case] = (useData[p.use_case] || 0) + (p.amount_php_mn || 0); });
  const usePie = Object.entries(useData).map(([name, value]) => ({ name, value }));

  // By sector
  const sectorData = {};
  projects.forEach(p => { sectorData[p.sector] = (sectorData[p.sector] || 0) + (p.amount_php_mn || 0); });
  const sectorChart = Object.entries(sectorData).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

  return (
    <div data-testid="green-finance" className="space-y-5 animate-fade-in">
      <div>
        <h1 className="font-heading font-light text-3xl text-content-primary tracking-tight">Green Finance Workbench</h1>
        <p className="text-sm text-content-secondary mt-1">Taxonomy alignment, sustainable finance flows, and adaptation pipelines</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { icon: CurrencyCircleDollar, label: 'Total Pipeline', val: `₱${(totalValue / 1000).toFixed(1)}B`, color: 'text-brand-primary', dk: 'gf_total_pipeline' },
          { icon: CheckCircle, label: 'Approved', val: `₱${(approvedValue / 1000).toFixed(1)}B`, color: 'text-status-on-track', dk: 'gf_approved' },
          { icon: Leaf, label: 'Adaptation Finance', val: `₱${(adaptationValue / 1000).toFixed(1)}B`, sub: `${((adaptationValue / Math.max(totalValue, 1)) * 100).toFixed(0)}% of total`, color: 'text-brand-accent', dk: 'gf_adaptation' },
          { icon: FunnelSimple, label: 'Green Classified', val: `${greenCount}/${projects.length}`, sub: 'taxonomy-qualified', color: 'text-status-on-track', dk: 'gf_green_classified' },
        ].map((k, i) => (
          <div key={i} onClick={() => setDrillKey(k.dk)} className="bg-white border border-border-default rounded-lg p-3.5 cursor-pointer hover:-translate-y-0.5 hover:shadow-sm transition-all group">
            <k.icon size={16} className={`${k.color} mb-1.5`} />
            <p className={`text-xl font-heading font-bold ${k.color}`}>{k.val}</p>
            <p className="text-[10px] text-content-secondary font-medium">{k.label}</p>
            {k.sub && <p className="text-[9px] text-content-disabled">{k.sub}</p>}
            <p className="text-[8px] text-brand-accent opacity-0 group-hover:opacity-100 transition-opacity mt-0.5">Click to drill down</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-border-default rounded-lg p-4">
          <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Taxonomy Classification</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={taxPie} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={4} dataKey="value">
                {taxPie.map((e, i) => <Cell key={i} fill={TAX_COLORS[e.name] || '#87A98E'} />)}
              </Pie>
              <Tooltip formatter={v => [`₱${v.toLocaleString()}M`]} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-border-default rounded-lg p-4">
          <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">By Use Case</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={usePie} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={4} dataKey="value">
                {usePie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={v => [`₱${v.toLocaleString()}M`]} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-border-default rounded-lg p-4">
          <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">By Sector (₱M)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={sectorChart} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F3F5" />
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={80} />
              <Tooltip formatter={v => [`₱${v.toLocaleString()}M`]} />
              <Bar dataKey="value" radius={[0, 3, 3, 0]} fill="#0A4D3C" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Project Pipeline Table */}
      <div className="bg-white border border-border-default rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-border-default">
          <h3 className="font-heading font-semibold text-sm text-content-primary">Sustainable Finance Pipeline</h3>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="gf-table" className="w-full text-[12px]">
            <thead><tr className="bg-surface-muted">
              {['Project', 'Region', 'Segment', 'Sector', 'Use Case', 'Taxonomy', 'Amount (₱M)', 'Instrument', 'Status'].map(h => (
                <th key={h} className="text-left px-4 py-2.5 font-semibold text-content-secondary text-[10px] uppercase tracking-wider">{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-border-subtle">
              {projects.map((p, i) => (
                <tr key={i} className="hover:bg-surface-muted/50 transition-colors">
                  <td className="px-4 py-2.5 font-mono text-[10px] font-medium text-brand-primary">{p.project_id}</td>
                  <td className="px-4 py-2.5">{p.region}</td>
                  <td className="px-4 py-2.5">{p.segment}</td>
                  <td className="px-4 py-2.5">{p.sector}</td>
                  <td className="px-4 py-2.5"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${p.use_case === 'Adaptation' ? 'bg-brand-accent/10 text-brand-accent' : 'bg-blue-50 text-blue-600'}`}>{p.use_case}</span></td>
                  <td className="px-4 py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: `${TAX_COLORS[p.taxonomy_class] || '#87A98E'}15`, color: TAX_COLORS[p.taxonomy_class] || '#87A98E' }}>{p.taxonomy_class}</span></td>
                  <td className="px-4 py-2.5 font-mono font-medium">{p.amount_php_mn?.toLocaleString()}</td>
                  <td className="px-4 py-2.5">{p.instrument}</td>
                  <td className="px-4 py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: `${STATUS_COLORS[p.status] || '#87A98E'}15`, color: STATUS_COLORS[p.status] || '#87A98E' }}>{p.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reference Metrics */}
      <div className="bg-white border border-border-default rounded-lg p-4">
        <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Market Reference Metrics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {reference_metrics?.map((m, i) => (
            <div key={i} className="p-3 bg-surface-default rounded-lg border border-border-subtle">
              <p className="text-[10px] text-content-secondary font-medium">{m.metric}</p>
              <p className="text-lg font-heading font-bold text-brand-primary mt-1">{m.value}</p>
            </div>
          ))}
        </div>
      </div>
      <DrillDownModal isOpen={!!drillKey} onClose={() => setDrillKey(null)} metricKey={drillKey} />
    </div>
  );
}
